import { useState, useEffect, useCallback, useRef } from 'react';
import type { CropMonitorRecord } from '@/types/api';
import { cropApi } from '@/services/cropApi';
import NdviBadge from '@/components/feature/NdviBadge';
import ImageModal from '@/components/feature/ImageModal';

interface HistoryTableProps {
  deviceId: string;
  selectedNo: number | null;
  onSelectRecord: (record: CropMonitorRecord) => void;
  onTotalCntChange?: (cnt: number) => void;
  refreshTrigger?: number;
}

const PAGE_SIZE = 20;

function formatCaptureTime(t: string): string {
  if (!t || t.length < 12) return t;
  return (
    t.substring(0, 2) + '.' +
    t.substring(2, 4) + '.' +
    t.substring(4, 6) + ' ' +
    t.substring(6, 8) + ':' +
    t.substring(8, 10) + ':' +
    t.substring(10, 12)
  );
}

const IMAGE_COLS = [
  { key: 'rgb1_url' as const, label: 'RGB1' },
  { key: 'rgb2_url' as const, label: 'RGB2' },
  { key: 'nir_url' as const, label: 'NIR' },
  { key: 'tof_url' as const, label: 'ToF' },
];

export default function HistoryTable({ deviceId, selectedNo, onSelectRecord, onTotalCntChange, refreshTrigger }: HistoryTableProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState<CropMonitorRecord[]>([]);
  const [totalCnt, setTotalCnt] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<{ url: string; label: string } | null>(null);
  const selectedRowRef = useRef<HTMLTableRowElement | null>(null);

  const fetchList = useCallback(
    async (pg: number, sdate: string, edate: string) => {
      if (!deviceId) return;
      setLoading(true);
      setError('');
      try {
        const res = await cropApi.getList(
          deviceId,
          pg,
          PAGE_SIZE,
          sdate || undefined,
          edate || undefined
        );
        if (res.result === 'success') {
          setRecords(res.list);
          setTotalCnt(res.total_cnt);
          setTotalPage(res.total_page || 1);
          onTotalCntChange?.(res.total_cnt);
        } else {
          setError(res.message || '목록을 불러오지 못했습니다.');
        }
      } catch {
        setError('목록 요청 실패. 서버를 확인해주세요.');
      } finally {
        setLoading(false);
      }
    },
    [deviceId]
  );

  useEffect(() => {
    fetchList(1, '', '');
  }, [fetchList, refreshTrigger]);

  const handleSearch = () => {
    setPage(1);
    fetchList(1, startDate, endDate);
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setPage(1);
    fetchList(1, '', '');
  };

  const handlePageChange = (pg: number) => {
    setPage(pg);
    fetchList(pg, startDate, endDate);
  };

  const handleDownloadAll = (record: CropMonitorRecord) => {
    IMAGE_COLS.forEach(col => {
      const url = record[col.key];
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${record.device_id}_${record.capture_no}_${col.label}.jpg`;
        a.target = '_blank';
        a.click();
      }
    });
  };

  const handleCaptureTimeClick = (record: CropMonitorRecord) => {
    onSelectRecord(record);
    // 상단으로 부드럽게 스크롤
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const delta = 2;
    for (let i = 1; i <= totalPage; i++) {
      if (i === 1 || i === totalPage || (i >= page - delta && i <= page + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div
          className="px-5 py-3.5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3"
          style={{ backgroundColor: '#f0f9f4' }}
        >
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-list-check text-green-600"></i>
            </div>
            <span className="font-bold text-gray-800 text-sm">촬영 히스토리</span>
            <span className="text-xs text-gray-400">총 {totalCnt}건</span>
            {loading && (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
              </div>
            )}
          </div>

          {/* Date Filter */}
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-green-500"
            />
            <span className="text-gray-400 text-xs">~</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-green-500"
            />
            <button
              onClick={handleSearch}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#1B4F72' }}
            >
              <i className="ri-search-line"></i>
              검색
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              <i className="ri-refresh-line"></i>
              초기화
            </button>
          </div>
        </div>

        {/* Hint */}
        <div className="px-5 py-2 bg-blue-50/60 border-b border-blue-100/60 flex items-center gap-1.5 text-blue-500 text-xs">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-information-line"></i>
          </div>
          <span>촬영시각을 클릭하면 해당 데이터를 상단에서 확인할 수 있습니다.</span>
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-red-500 text-xs flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: '#f8fffe' }}>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-600 whitespace-nowrap">No</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-600 whitespace-nowrap">
                  촬영시각
                  <span className="ml-1 text-blue-400 font-normal">(클릭)</span>
                </th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-600 whitespace-nowrap">ToF거리(mm)</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-600 whitespace-nowrap">벼높이(mm)</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-600 whitespace-nowrap">NDVI</th>
                {IMAGE_COLS.map(c => (
                  <th key={c.key} className="px-3 py-2.5 text-center font-semibold text-gray-600 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-left font-semibold text-gray-600 whitespace-nowrap">수신시각</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-600 whitespace-nowrap">다운로드</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    {Array.from({ length: 10 }).map((__, j) => (
                      <td key={j} className="px-3 py-3">
                        <div className="h-4 bg-gray-100 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-inbox-line text-4xl"></i>
                      </div>
                      <span>데이터가 없습니다.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((r, index) => {
                  const isHighlighted = selectedNo !== null && r.no === selectedNo;
                  return (
                    <tr
                      key={r.no}
                      ref={isHighlighted ? selectedRowRef : null}
                      className="border-t border-gray-50 transition-colors duration-200"
                      style={{
                        backgroundColor: isHighlighted ? '#EBF5FB' : undefined,
                      }}
                      onMouseEnter={e => {
                        if (!isHighlighted) {
                          (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '#f0faf4';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isHighlighted) {
                          (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                        }
                      }}
                    >
                      <td className="px-3 py-2 text-gray-500">{r.seq}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          onClick={() => handleCaptureTimeClick(r)}
                          className="cursor-pointer text-blue-600 underline underline-offset-2 hover:text-blue-800 transition-colors font-medium bg-transparent border-none p-0"
                          title="클릭하면 상단에 해당 데이터가 표시됩니다"
                        >
                          {formatCaptureTime(r.capture_time)}
                        </button>
                        {isHighlighted && (
                          <span
                            className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-white font-bold"
                            style={{ fontSize: '9px', backgroundColor: '#2E86C1' }}
                          >
                            표시중
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-700">
                        {Number(r.tof_center_mm).toFixed(0)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-700">
                        {Number(r.rice_height_mm).toFixed(0)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <NdviBadge value={Number(r.ndvi_avg)} />
                      </td>
                      {IMAGE_COLS.map(col => {
                        const url = r[col.key];
                        return (
                          <td key={col.key} className="px-3 py-2 text-center">
                            {url ? (
                              <div
                                className="w-[60px] h-[45px] mx-auto rounded overflow-hidden cursor-pointer border border-gray-200 hover:border-green-400 transition-colors"
                                onClick={() =>
                                  setModal({ url, label: `${col.label} - #${r.capture_no}` })
                                }
                              >
                                <img
                                  src={url}
                                  alt={col.label}
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                            ) : (
                              <div className="w-[60px] h-[45px] mx-auto rounded bg-gray-100 flex items-center justify-center text-gray-300">
                                <i className="ri-image-line text-sm"></i>
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.regdate}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleDownloadAll(r)}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg text-white text-xs cursor-pointer whitespace-nowrap mx-auto"
                          style={{ backgroundColor: '#27ae60' }}
                          title="4장 이미지 다운로드"
                        >
                          <i className="ri-download-2-line"></i>
                          다운
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPage > 1 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-center gap-1">
            <button
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 cursor-pointer hover:bg-gray-50"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {pageNumbers().map((p, i) =>
              typeof p === 'number' ? (
                <button
                  key={i}
                  onClick={() => handlePageChange(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer transition-all"
                  style={
                    page === p
                      ? { backgroundColor: '#1B4F72', color: '#fff' }
                      : { border: '1px solid #e5e7eb', color: '#374151' }
                  }
                >
                  {p}
                </button>
              ) : (
                <span
                  key={i}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 text-xs"
                >
                  ...
                </span>
              )
            )}
            <button
              onClick={() => handlePageChange(Math.min(totalPage, page + 1))}
              disabled={page === totalPage}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 cursor-pointer hover:bg-gray-50"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      {modal && (
        <ImageModal
          isOpen={true}
          imageUrl={modal.url}
          label={modal.label}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
